import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { CheckCircle, XCircle, User, Building2, Phone, MapPin, Calendar, Droplet, GraduationCap, Briefcase } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VerifyIdCardPage = () => {
  const { code } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const verifyCard = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/id-cards/verify/${code}`);
        setData(res.data);
      } catch (err) {
        setError(err.response?.data?.detail || 'Verification failed');
      } finally {
        setLoading(false);
      }
    };
    
    if (code) {
      verifyCard();
    }
  }, [code]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Verifying ID Card...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-pink-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-red-200">
          <CardContent className="p-8 text-center">
            <XCircle className="h-16 w-16 text-red-500 mx-auto" />
            <h2 className="text-2xl font-bold text-red-700 mt-4">Verification Failed</h2>
            <p className="text-gray-600 mt-2">{error}</p>
            <p className="text-sm text-gray-400 mt-4">
              This ID card could not be verified. It may be invalid or expired.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { school, person, type, verified_at } = data;

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-md border-green-200 shadow-xl">
        <CardHeader className="bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-t-lg">
          <div className="flex items-center gap-3">
            <CheckCircle className="h-10 w-10" />
            <div>
              <CardTitle className="text-xl">ID Card Verified</CardTitle>
              <p className="text-green-100 text-sm">
                {type === 'student' ? 'Student' : 'Staff'} Identity Confirmed
              </p>
            </div>
          </div>
        </CardHeader>
        
        <CardContent className="p-6 space-y-6">
          {/* School Info */}
          <div className="flex items-center gap-4 pb-4 border-b">
            {school.logo ? (
              <img 
                src={school.logo} 
                alt="School Logo" 
                className="h-16 w-16 object-contain rounded-lg bg-gray-100 p-1"
              />
            ) : (
              <div className="h-16 w-16 bg-blue-100 rounded-lg flex items-center justify-center">
                <Building2 className="h-8 w-8 text-blue-600" />
              </div>
            )}
            <div>
              <h3 className="font-bold text-lg text-gray-900">{school.name}</h3>
              {school.address && (
                <p className="text-sm text-gray-500 flex items-center gap-1">
                  <MapPin className="h-3 w-3" /> {school.address}
                </p>
              )}
              {school.phone && (
                <p className="text-sm text-gray-500 flex items-center gap-1">
                  <Phone className="h-3 w-3" /> {school.phone}
                </p>
              )}
            </div>
          </div>

          {/* Person Info */}
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              {person.photo ? (
                <img 
                  src={person.photo} 
                  alt={person.name}
                  className="h-20 w-20 object-cover rounded-full border-4 border-green-200"
                />
              ) : (
                <div className="h-20 w-20 bg-gray-200 rounded-full flex items-center justify-center">
                  <User className="h-10 w-10 text-gray-400" />
                </div>
              )}
              <div>
                <h2 className="text-xl font-bold text-gray-900">{person.name}</h2>
                <Badge variant={person.status === 'active' ? 'default' : 'destructive'} className="mt-1">
                  {person.status?.toUpperCase() || 'ACTIVE'}
                </Badge>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                <span className="text-gray-500">ID:</span>
                <span className="font-semibold">{person.id}</span>
              </div>
              
              {type === 'student' ? (
                <>
                  <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                    <GraduationCap className="h-4 w-4 text-blue-500" />
                    <span className="font-semibold">{person.class} {person.section && `- ${person.section}`}</span>
                  </div>
                  
                  {person.dob && (
                    <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                      <Calendar className="h-4 w-4 text-purple-500" />
                      <span>{person.dob}</span>
                    </div>
                  )}
                  
                  {person.blood_group && (
                    <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                      <Droplet className="h-4 w-4 text-red-500" />
                      <span className="font-semibold">{person.blood_group}</span>
                    </div>
                  )}
                  
                  {person.father_name && (
                    <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg col-span-2">
                      <User className="h-4 w-4 text-gray-500" />
                      <span>Father: <strong>{person.father_name}</strong></span>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                    <Briefcase className="h-4 w-4 text-amber-500" />
                    <span className="capitalize">{person.role?.replace('_', ' ')}</span>
                  </div>
                  
                  {person.department && (
                    <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                      <Building2 className="h-4 w-4 text-indigo-500" />
                      <span>{person.department}</span>
                    </div>
                  )}
                  
                  {person.designation && (
                    <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg col-span-2">
                      <span className="text-gray-500">Designation:</span>
                      <span className="font-semibold">{person.designation}</span>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Verification Timestamp */}
          <div className="text-center pt-4 border-t text-xs text-gray-400">
            <p>Verified at: {new Date(verified_at).toLocaleString()}</p>
            <p className="mt-1">Powered by CloudCampus ERP</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default VerifyIdCardPage;
